import { useState, useEffect, useRef, useCallback } from 'react';
import { getMessages } from '../../../services/chatService';
import { streamManager } from '../../../services/streamManager';
import { Message as MessageType, UIMessage, Artifact } from '../../../types/chat';
import { useArtifacts, useStreamingArtifact, extractArtifact } from '../../artifacts';
import { ERROR_MESSAGES, CHAT_CONFIG, STORAGE_KEYS } from '../constants';

interface UseChatMessagesReturn {
  messages: UIMessage[];
  isLoading: boolean;
  isInitializing: boolean;
  error: string | null;
  userScrolled: boolean;
  activeArtifact: Artifact | null;
  isArtifactLoading: boolean;
  isUpdating: boolean;
  tokenLimitError: TokenLimitError | null;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  setUserScrolled: (value: boolean) => void;
  setActiveArtifact: (artifact: Artifact | null) => void;
  setError: (error: string | null) => void;
  sendMessage: (message: string, files?: File[]) => Promise<void>;
  retryMessage: (message: UIMessage) => Promise<void>;
  closeTokenLimitModal: () => void;
}

interface TokenLimitError {
  error?: string;
  message?: string;
  tokens_used?: number;
  token_limit?: number;
  estimated_usage?: number;
  tokens_remaining?: number;
  upgrade_url?: string;
}

/**
 * useChatMessages - Hook para gerenciar mensagens do chat
 */
export const useChatMessages = (sessionId: string | undefined): UseChatMessagesReturn => {
  // State
  const [messages, setMessages] = useState<UIMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userScrolled, setUserScrolled] = useState(false);
  const [isArtifactLoading, setIsArtifactLoading] = useState(false);
  const [tokenLimitError, setTokenLimitError] = useState<TokenLimitError | null>(null);

  // Artifact management
  const {
    activeArtifact,
    isUpdating,
    addOrUpdateArtifact,
    setActiveArtifact,
    clearArtifacts,
  } = useArtifacts();

  const { processStreamingText, clearArtifact } = useStreamingArtifact();

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageStreamRef = useRef<boolean>(false);
  const hasStreamingPlaceholderRef = useRef<boolean>(false);

  // Reset on session change
  useEffect(() => {
    setError(null);
    setMessages([]); // Clear messages immediately
    setIsInitializing(true); // Show loading overlay
    clearArtifacts();
    messageStreamRef.current = false;
    hasStreamingPlaceholderRef.current = false;
  }, [sessionId, clearArtifacts]);

  // Load existing messages
  const loadExistingMessages = useCallback(async () => {
    if (!sessionId) return;

    const pendingQuestion = localStorage.getItem(STORAGE_KEYS.pendingChatQuestion);
    if (pendingQuestion) {
      setIsInitializing(false);
      return;
    }

    try {
      const response = await getMessages(Number(sessionId));
      if (response.success && Array.isArray(response.data)) {
        const artifactsMap = new Map<string, Artifact>();

        let formattedMessages = response.data.flatMap((msg: MessageType) => {
          const messages = [] as UIMessage[];
          if (msg.question) {
            messages.push({
              id: msg.id * 2,
              text: msg.question,
              sender: 'user' as const,
              timestamp: msg.created_at,
            });
          }
          if (msg.answer) {
            const extractionResult = extractArtifact(msg.answer, {
              messageId: msg.id,
              existingArtifacts: artifactsMap,
            });
            messages.push({
              id: msg.id * 2 + 1,
              text: extractionResult.cleanText,
              rawText: msg.answer, // Preserve original text for copy functionality
              sender: 'ai' as const,
              timestamp: msg.created_at,
              artifact: extractionResult.artifact,
            });

            if (extractionResult.artifact) {
              artifactsMap.set(extractionResult.artifact.id, extractionResult.artifact);
              addOrUpdateArtifact(extractionResult.artifact, { preserveScroll: false });
            }
          }
          return messages;
        });

        // Merge active streaming buffer
        const sid = Number(sessionId);
        const snapshot = streamManager.getSnapshot(sid);
        const reading = streamManager.isReading(sid);
        if ((reading || snapshot.content) && formattedMessages.length > 0) {
          const last = formattedMessages[formattedMessages.length - 1];
          if (last.sender === 'user') {
            formattedMessages = [
              ...formattedMessages,
              {
                id: Date.now(),
                text: snapshot.content || '',
                sender: 'ai' as const,
                timestamp: new Date().toISOString(),
              },
            ];
            hasStreamingPlaceholderRef.current = true;
          }
        }

        setMessages(formattedMessages);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : ERROR_MESSAGES.loadMessages);
    } finally {
      setIsInitializing(false);
    }
  }, [sessionId, addOrUpdateArtifact]);

  // Handle initial conversation
  const handleInitialConversation = useCallback(async () => {
    const pendingQuestion = localStorage.getItem(STORAGE_KEYS.pendingChatQuestion);
    if (pendingQuestion && !messageStreamRef.current) {
      messageStreamRef.current = true;
      localStorage.removeItem(STORAGE_KEYS.pendingChatQuestion);

      let pendingFiles: File[] | undefined;
      try {
        const pendingFilesInfo = sessionStorage.getItem(STORAGE_KEYS.pendingChatFiles);
        if (pendingFilesInfo) {
          sessionStorage.removeItem(STORAGE_KEYS.pendingChatFiles);
          const windowFiles = (window as unknown as { pendingFiles?: File[] }).pendingFiles;
          if (windowFiles && Array.isArray(windowFiles)) {
            pendingFiles = windowFiles;
            delete (window as unknown as { pendingFiles?: File[] }).pendingFiles;
          }
        }
      } catch (e) {
        console.error('Error loading pending files:', e);
      }

      const userMessage = {
        id: Date.now(),
        text: pendingQuestion,
        sender: 'user' as const,
        timestamp: new Date().toISOString(),
        files: pendingFiles?.map((f) => ({ name: f.name, size: f.size, type: f.type })),
      };

      const aiMessage = {
        id: Date.now() + 1,
        text: '',
        sender: 'ai' as const,
        timestamp: new Date().toISOString(),
      };

      setMessages([userMessage, aiMessage]);

      try {
        hasStreamingPlaceholderRef.current = true;
        await streamManager.startStream(parseInt(sessionId!), pendingQuestion, pendingFiles);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : ERROR_MESSAGES.sendInitialMessage);
        messageStreamRef.current = false;
        hasStreamingPlaceholderRef.current = false;
      }
    }
  }, [sessionId]);

  // Initialize chat
  useEffect(() => {
    const initializeChat = async () => {
      if (!sessionId) return;
      if (!messageStreamRef.current) {
        await loadExistingMessages();
        await handleInitialConversation();
      }
    };
    initializeChat();
  }, [sessionId, loadExistingMessages, handleInitialConversation]);

  // Subscribe to stream updates
  useEffect(() => {
    if (!sessionId) return;
    const sid = parseInt(sessionId);

    const unsubscribe = streamManager.subscribe(sid, (event) => {
      if (event.type === 'snapshot') {
        if (!event.content) return;
        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          const { cleanText, artifact } = processStreamingText(event.content);

          if (!hasStreamingPlaceholderRef.current) {
            if (last && last.sender === 'user') {
              updated.push({
                id: Date.now(),
                text: cleanText,
                rawText: event.content,
                sender: 'ai',
                timestamp: new Date().toISOString(),
                streamingArtifact: artifact,
              });
              hasStreamingPlaceholderRef.current = true;
              return updated;
            }
          }
          if (last && last.sender === 'ai') {
            updated[updated.length - 1] = {
              ...last,
              text: cleanText,
              rawText: event.content,
              streamingArtifact: artifact,
            };
          }
          return updated;
        });
      } else if (event.type === 'chunk') {
        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last && last.sender === 'ai') {
            const currentRawText = last.rawText || last.text;
            const newRawText = currentRawText + event.content;
            const { cleanText, artifact } = processStreamingText(newRawText);

            if (artifact && artifact.isComplete && !last.artifact) {
              const finalArtifact: Artifact = {
                id: artifact.id,
                type: artifact.type,
                title: artifact.title,
                content: artifact.content,
                language: artifact.language,
                messageId: last.id,
                createdAt: new Date().toISOString(),
              };

              addOrUpdateArtifact(finalArtifact, {
                preserveScroll: false,
                animateTransition: true,
              });
              setActiveArtifact(finalArtifact);

              updated[updated.length - 1] = {
                ...last,
                rawText: newRawText,
                text: cleanText,
                artifact: finalArtifact,
                streamingArtifact: undefined,
              };
            } else if (artifact) {
              updated[updated.length - 1] = {
                ...last,
                rawText: newRawText,
                text: cleanText,
                streamingArtifact: artifact,
              };
            } else {
              updated[updated.length - 1] = {
                ...last,
                rawText: newRawText,
                text: cleanText,
              };
            }
          }
          return updated;
        });
      } else if (event.type === 'done') {
        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];

          if (last && last.sender === 'ai') {
            const textToProcess = last.rawText || last.text;
            const { cleanText, artifact } = processStreamingText(textToProcess);

            if (artifact) {
              const finalArtifact: Artifact = {
                id: artifact.id,
                type: artifact.type,
                title: artifact.title,
                content: artifact.content,
                language: artifact.language,
                messageId: event.messageId || last.id,
                createdAt: new Date().toISOString(),
              };

              addOrUpdateArtifact(finalArtifact, {
                preserveScroll: false,
                animateTransition: false,
              });
              setActiveArtifact(finalArtifact);

              updated[updated.length - 1] = {
                ...last,
                rawText: textToProcess, // Keep raw text for copy functionality
                text: cleanText,
                artifact: finalArtifact,
                streamingArtifact: undefined,
              };
            } else {
              updated[updated.length - 1] = {
                ...last,
                rawText: textToProcess, // Keep raw text for copy functionality
                text: cleanText,
                streamingArtifact: undefined,
              };
            }
          }

          return updated;
        });

        setIsArtifactLoading(false);
        clearArtifact();
        messageStreamRef.current = false;
        hasStreamingPlaceholderRef.current = false;
      } else if (event.type === 'error') {
        if (event.tokenLimitError) {
          setTokenLimitError(event.tokenLimitError);
        } else {
          setError(event.error);
        }

        setMessages((prev) => {
          const updated = [...prev];
          let handled = false;
          for (let i = updated.length - 1; i >= 0; i--) {
            if (updated[i].sender === 'user' && updated[i].status === 'sending') {
              updated[i] = {
                ...updated[i],
                status: 'error' as const,
                errorMessage: event.error,
              };
              if (updated[i + 1] && updated[i + 1].sender === 'ai' && updated[i + 1].status === 'sending') {
                updated.splice(i + 1, 1);
              }
              handled = true;
              break;
            }
          }

          // Sem nenhuma mensagem em 'sending' — caso da primeira pergunta, que
          // vem do NewChatPage sem status — nada seria marcado e a bolha vazia
          // da IA continuaria girando. Marca a última bolha da IA como erro.
          if (!handled) {
            const lastIndex = updated.length - 1;
            const last = updated[lastIndex];
            if (last && last.sender === 'ai' && !last.text && !last.artifact) {
              updated[lastIndex] = {
                ...last,
                status: 'error' as const,
                errorMessage: event.error,
              };
            }
          }

          return updated;
        });

        messageStreamRef.current = false;
        hasStreamingPlaceholderRef.current = false;
        setIsArtifactLoading(false);
      }
    });

    return () => unsubscribe();
  }, [sessionId, processStreamingText, clearArtifact, addOrUpdateArtifact, setActiveArtifact]);

  // Auto-scroll
  useEffect(() => {
    if (messages.length > 0 && !userScrolled && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, userScrolled]);

  // Send message
  const sendMessage = useCallback(async (message: string, files?: File[]) => {
    if (!message.trim() || !sessionId || isLoading) return;

    setIsLoading(true);
    setError(null);

    const userMessage: UIMessage = {
      id: Date.now(),
      text: message,
      sender: 'user',
      timestamp: new Date().toISOString(),
      status: 'sending',
      files: files?.map((f) => ({ name: f.name, size: f.size, type: f.type })),
      hadFiles: files && files.length > 0,
    };

    const aiMessage: UIMessage = {
      id: Date.now() + 1,
      text: '',
      sender: 'ai',
      timestamp: new Date().toISOString(),
      status: 'sending',
    };

    setMessages((prev) => [...prev, userMessage, aiMessage]);

    try {
      hasStreamingPlaceholderRef.current = true;
      await streamManager.startStream(parseInt(sessionId), message, files);

      // startStream resolve normalmente mesmo quando o backend reporta erro (a
      // falha é emitida via subscribe, não lançada). Sem a checagem de 'error'
      // este map sobrescreveria o status que o handler acabou de marcar.
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === userMessage.id && msg.status !== 'error'
            ? { ...msg, status: 'sent' as const }
            : msg
        )
      );

      window.dispatchEvent(new CustomEvent('messageSent'));
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : ERROR_MESSAGES.sendMessage;

      setMessages((prev) =>
        prev
          .map((msg) =>
            msg.id === userMessage.id
              ? { ...msg, status: 'error' as const, errorMessage }
              : msg.id === aiMessage.id
              ? null
              : msg
          )
          .filter(Boolean) as UIMessage[]
      );

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, isLoading]);

  // Retry message
  const retryMessage = useCallback(async (failedMessage: UIMessage) => {
    if (!sessionId || isLoading) return;

    const retryCount = (failedMessage.retryCount || 0) + 1;
    if (retryCount > CHAT_CONFIG.retryLimit) {
      setError(ERROR_MESSAGES.retryLimit);
      return;
    }

    setIsLoading(true);
    setError(null);

    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === failedMessage.id
          ? { ...msg, status: 'sending' as const, errorMessage: undefined, retryCount }
          : msg
      )
    );

    const aiMessage: UIMessage = {
      id: Date.now(),
      text: '',
      sender: 'ai',
      timestamp: new Date().toISOString(),
      status: 'sending',
    };

    setMessages((prev) => [...prev, aiMessage]);

    try {
      hasStreamingPlaceholderRef.current = true;
      await streamManager.startStream(parseInt(sessionId), failedMessage.text);

      // Mesma checagem do sendMessage: não promover para 'sent' uma mensagem
      // que o handler de erro acabou de marcar como falha.
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === failedMessage.id && msg.status !== 'error'
            ? { ...msg, status: 'sent' as const }
            : msg
        )
      );
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : ERROR_MESSAGES.retryMessage;

      setMessages((prev) =>
        prev
          .map((msg) =>
            msg.id === failedMessage.id
              ? { ...msg, status: 'error' as const, errorMessage }
              : msg.id === aiMessage.id
              ? null
              : msg
          )
          .filter(Boolean) as UIMessage[]
      );

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, isLoading]);

  const closeTokenLimitModal = useCallback(() => {
    setTokenLimitError(null);
    // Reset loading state to prevent "thinking" indicator from staying stuck
    setIsLoading(false);
    // Remove empty AI message placeholders (thinking indicator)
    setMessages((prev) => prev.filter((msg) => {
      // Keep all user messages
      if (msg.sender === 'user') return true;
      // Keep AI messages that have text or artifacts
      if (msg.sender === 'ai') {
        return msg.text || msg.artifact || msg.streamingArtifact;
      }
      return true;
    }));
    // Reset streaming refs
    hasStreamingPlaceholderRef.current = false;
    messageStreamRef.current = false;
  }, []);

  return {
    messages,
    isLoading,
    isInitializing,
    error,
    userScrolled,
    activeArtifact,
    isArtifactLoading,
    isUpdating,
    tokenLimitError,
    messagesEndRef,
    setUserScrolled,
    setActiveArtifact,
    setError,
    sendMessage,
    retryMessage,
    closeTokenLimitModal,
  };
};
